import { useState, useEffect } from "react";
import MainLogo from "../img/LogoO@2xblk3.png";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Link as ScrollLink } from "react-scroll";
import { CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  IconButton,
  Image,
  Stack,
  useBreakpointValue,
  useDisclosure,
  Portal,
  Text,
  Spacer,
} from "@chakra-ui/react";

export default function Nav() {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isScrolled, setIsScrolled] = useState(false);

  const brandBlue = "#3D53A4";
  const brandGray = "#CDC7C3";
  const textColor = isScrolled ? "#0F1119" : "#ffffff";

  const imageSrc = useBreakpointValue({ base: MainLogo, md: MainLogo });

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleLang = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    if (isOpen) onClose();
  };

  const menuItems = [
    { label: t("navbar.home"), to: "hero" },
    { label: t("navbar.about"), to: "overview" },
    { label: t("navbar.visionMission"), to: "vision-mission" },
    { label: t("navbar.businesses"), to: "businesses" },
    { label: t("navbar.careers"), to: "careers" },
    { label: t("navbar.contact"), to: "contact" },
  ];

  const displayedMenuItems =
    i18n.language === "ar" ? [...menuItems].reverse() : menuItems;

  return (
    <Box
      as="header"
      dir="ltr"
      position="fixed"
      top="0"
      left="0"
      w="100%"
      zIndex="2000"
      transition="all 0.4s ease"
      bg={
        isScrolled
          ? "rgba(255, 255, 255, 0.85)" 
          : "transparent"
      }
      backdropFilter={isScrolled ? "blur(10px)" : "none"}
      boxShadow={isScrolled ? "0 6px 25px rgba(0,0,0,0.08)" : "none"}
      borderBottom={isScrolled ? "1px solid rgba(255,255,255,0.1)" : "none"}
    >

      <Container
        maxW="container.xl"
        py={{ base: 3, md: 2, lg: 2 }}
        minH={{ base: "60px", md: "70px", lg: "72px" }}
      >
        <Flex align="center" w="100%">

          <Flex align="center" flexShrink={0}>
            <Image
              src={MainLogo}
              alt="Alpha Union Logo"
              h={useBreakpointValue({
                base: "80px",
                md: "100px",
                lg: "100px",
              })}
              w="auto"
              transition="all 0.3s ease"
              filter={isScrolled ? "brightness(0.9)" : "brightness(1)"}
              mr={{ base: 2, md: 3 }}
            />
          </Flex>

          <Spacer />

          <HStack
            as="nav"
            spacing={7}
            display={{ base: "none", md: "flex" }}
            align="center"
            justify="flex-end"
          >
            {displayedMenuItems.map((item, i) => (
              <ScrollLink key={i} to={item.to} smooth duration={700} offset={-80}>
                <Text
                  dir="auto"
                  color={textColor}
                  fontWeight="medium"
                  fontSize="sm"
                  letterSpacing="0.2px"
                  cursor="pointer"
                  transition="all 0.3s ease"
                  _hover={{ color: brandBlue }}
                >
                  {item.label}
                </Text>
              </ScrollLink>
            ))}

            <Button
              size="sm"
              variant="outline"
              borderColor={textColor}
              color={textColor}
              _hover={{ bg: brandBlue, color: "white", borderColor: brandBlue }}
              onClick={toggleLang}
              transition="all 0.3s ease"
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </HStack>


          <IconButton
            aria-label="Toggle Menu"
            bg="transparent"
            color={textColor}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            display={{ base: "flex", md: "none" }}
            onClick={isOpen ? onClose : onOpen}
            fontSize="22px"
            _hover={{ bg: "rgba(255,255,255,0.1)" }}
            ml={3}
          />
        </Flex>
      </Container>

     
      <Portal>
        <Box
          position="fixed"
          top={isOpen ? "0" : "-100vh"}
          left="0"
          w="100%"
          h="100vh"
          bg="rgba(15,17,25,0.96)"
          backdropFilter="blur(16px)"
          transition="top 0.4s ease"
          zIndex="1999"
          display={{ base: "flex", md: "none" }}
          flexDir="column"
          alignItems="center"
          justifyContent="center"
        >
          <Stack spacing={7} align="center">
            {menuItems.map((item, index) => (
              <ScrollLink
                key={index}
                to={item.to}
                smooth
                duration={700}
                offset={-80}
                onClick={onClose}
              >
                <Text
                  dir="auto"
                  color="white"
                  fontSize="2xl"
                  fontWeight="semibold"
                  transition="color 0.3s ease"
                  _hover={{
                    color: brandBlue,
                    textShadow: `0 0 8px ${brandBlue}`,
                  }}
                >
                  {item.label}
                </Text>
              </ScrollLink>
            ))}

            <Button
              mt={6}
              size="md"
              variant="outline"
              borderColor={brandGray}
              color={brandGray}
              _hover={{
                bg: brandBlue,
                color: "white",
                borderColor: brandBlue,
              }}
              onClick={toggleLang}
            >
              {i18n.language === "ar" ? "English" : "عربي"}
            </Button>
          </Stack>
        </Box>
      </Portal>
    </Box>
  );
}
